import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { InfoButton } from '../../../components/connectedForm/Button';
import {
  AlertType,
  FilterOperation,
  IAlert,
  IPurchaseOrderDocumentGenerateRangeMutationResult,
  IPurchaseOrderDocumentGenerateRangeMutationVariables,
  IPurchaseOrdersAggregateQueryResult,
  IPurchaseOrdersAggregateQueryVariables,
  ISalesOrder,
} from '../../../graphql/schema/p4types';

import PurchaseOrderAggregateQuery from '../../../graphql/queries/purchases/PurchaseOrderAggregateQuery.graphql';
import PurchaseOrderGenerateRangeMutation from '../../../graphql/mutations/purchases/PurchaseOrderDocumentGenerateRangeMutation.graphql';
import { useDispatch } from 'react-redux';
import DocumentModal2 from '../../../components/modals/DocumentModal2';
import PurchaseOrderDocumentPrintForm from '../../purchases/PurchaseOrderDocumentPrintForm';
import { INCLUSION_METHOD_OPTIONS, InclusionMethod } from '../../purchases/DocumentPrintHeader';
import generateClientMutationId from '../../../graphql/schema/generateClientMutationId';
import PurchaseOrderDocument from '../../../graphql/entities/purchases/PurchaseOrderDocument';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { usePrintMutation } from '../../../utils/BackgroundJobs';
import { useP4ToastController } from '../../system/NotificationsArea';
import Notification from '../../../components/Notification';
import FluentModal from '../../../components/modals/FluentModal';

/**
 * Notification component that allows view or printing of Purchase Orders generated against lines during Sales Order entry.
 * @param onClose - Function to close the alert.
 * @param alert - Alert detail - We need this to get the UUID of the alert which we can use to determine the generated Purchase Orders.
 */
const SalesOrderPurchaseOrdersNotification = ({ onClose, alert }: { onClose: () => void; alert: IAlert }) => {
  const [printIsOpen, setPrintIsOpen] = useState(false);
  const [viewIsOpen, setViewIsOpen] = useState(false);
  const purchaseOrderFilters = useMemo(() => {
    return [
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'jobId',
          value: alert.uuid,
        },
      },
    ];
  }, [alert.uuid]);

  //Note - Uses the aggregation version of the query because we don't need any row data - Just the count.
  const { data } = useQuery<IPurchaseOrdersAggregateQueryResult, IPurchaseOrdersAggregateQueryVariables>(
    PurchaseOrderAggregateQuery,
    {
      variables: {
        filters: purchaseOrderFilters,
      },
    }
  );

  //Mutation to print the Purchase Orders - Using the 'usePrintMutation' hook to handle summary display if requested.
  const [printPurchaseOrders] = usePrintMutation<
    IPurchaseOrderDocumentGenerateRangeMutationResult,
    IPurchaseOrderDocumentGenerateRangeMutationVariables
  >(PurchaseOrderGenerateRangeMutation);

  const notificationActionOptions = [
    <InfoButton key={'resultsOption'} onClick={() => setViewIsOpen(true)}>
      View Purchase Orders
    </InfoButton>,
    <InfoButton
      key={'printLabels'}
      onClick={() => {
        setPrintIsOpen(true);
      }}
    >
      Print Orders
    </InfoButton>,
  ];

  return (
    <>
      <DocumentModal2
        title={'Print Purchase Orders'}
        multiValue={true}
        entity={PurchaseOrderDocument}
        allowEmail={true}
        onClose={() => setPrintIsOpen(false)}
        modalOpen={printIsOpen}
        modalWrapper={true}
        onPrintClick={({ reportParameters, documentState }) => {
          printPurchaseOrders(
            {
              variables: {
                input: {
                  clientMutationId: generateClientMutationId(),
                  filters: purchaseOrderFilters,
                  purchaseOrders: [],
                  include: false,
                  reportParameters: normalizeVariables(reportParameters),
                },
              },
            },
            { showSummary: documentState.showSummary }
          );
        }}
        count={data?.listData?.count ?? 0}
        useCustomerDefaults={true}
      />
      <FluentModal isOpen={viewIsOpen}>
        <div style={{ position: 'relative', height: '97vh', width: '80vw' }}>
          <PurchaseOrderDocumentPrintForm
            predefinedFilters={purchaseOrderFilters}
            inclusionMethodOptions={INCLUSION_METHOD_OPTIONS.filter((value) => value.value === InclusionMethod.New)}
            onClose={() => setViewIsOpen(false)}
          />
        </div>
      </FluentModal>
      {viewIsOpen || printIsOpen ? null : (
        <Notification
          actionButtons={notificationActionOptions}
          title={'Purchase Order Generation'}
          message={'Print or View Purchase Orders'}
          type={AlertType.Action}
        />
      )}
    </>
  );
};

/**
 * Hook to provide an onSuccess function for SalesOrderForm to fire the PO print notification after any given save.
 */
export const usePurchaseOrderPrintOnSuccess = () => {
  const dispatch = useDispatch();
  const { dispatchToast, dismissToast } = useP4ToastController();

  return (result?: ISalesOrder) => {
    result?.entityMetaData?.alerts?.forEach((alert) => {
      //Process PO Print Alert.
      if (alert?.uuid && alert.alertType === AlertType.Action && alert.title === 'Purchase Order Generation') {
        const purchaseOrderPrintToastId = `${alert.uuid}-print`;

        dispatchToast(
          <SalesOrderPurchaseOrdersNotification
            onClose={() => {
              dismissToast(purchaseOrderPrintToastId);
            }}
            alert={alert}
          />,
          {
            title: 'Print or View Purchase Orders',
            type: AlertType.Action,
            toastId: purchaseOrderPrintToastId,
            intent: 'success',
          }
        );
      }
    });
  };
};
